<template>
	<div class="app-sidebar bg-white rounded-2">
		<nav :class="{ 'sidebar-collapsed': collapsed }">
			<!-- Logo / Brand -->
			<div class="logo text-center fixed-logo">
				<div class="logo-content" v-show="!collapsed">
					<h3 class="logo-text">Town Web</h3>
					<p class="logo-text-sub">Juniper CMS</p>
				</div>
				<!-- Panel-left close/open icon (Lucide-style). The vertical line inside the rectangle shifts: expanded → line on LEFT (full panel visible); collapsed → line on RIGHT (panel minimized). Rendered as a bare clickable SVG — no surrounding button chrome, just the icon. -->
				<svg
					viewBox="0 0 18 18"
					class="sidebar-toggle-icon d-none d-md-block"
					role="button"
					tabindex="0"
					:aria-label="collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
					@click.stop="toggleCollapsed"
					@keydown.enter.stop.prevent="toggleCollapsed"
					@keydown.space.stop.prevent="toggleCollapsed"
				>
					<title>{{ collapsed ? 'Expand Sidebar' : 'Collapse Sidebar' }}</title>
					<rect x="2.5" y="3.5" width="13" height="11" rx="2.25" fill="none" stroke="currentColor" stroke-width="1.4" />
					<line :x1="collapsed ? 12 : 6" y1="3.5" :x2="collapsed ? 12 : 6" y2="14.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
				</svg>
			</div>

			<!-- Scrollable nav: top + middle (active) + soon, single flat list, no dividers -->
			<div class="scrollable-nav">
				<ul class="list-unstyled mb-0 juniper-nav-list">
					<!-- TOP group -->
					<li v-for="item in topNavItems" :key="item.path">
						<router-link
							class="btn juniper-nav-item"
							:class="{ 'router-link-exact-active router-link-active': isItemActive(item) }"
							:to="item.path"
						>
							<font-awesome-icon :icon="item.iconRef" class="juniper-nav-icon" />
							<span v-show="!collapsed" class="juniper-nav-label">{{ item.label }}</span>
						</router-link>
					</li>

					<!-- MIDDLE group (alpha-sorted active features) -->
					<li v-for="item in middleNavItems" :key="item.path">
						<router-link
							class="btn juniper-nav-item"
							:class="{ 'router-link-exact-active router-link-active': isItemActive(item) }"
							:to="item.path"
						>
							<font-awesome-icon :icon="item.iconRef" class="juniper-nav-icon" />
							<span v-show="!collapsed" class="juniper-nav-label">{{ item.label }}</span>
							<span v-if="item.badge && !collapsed" class="badge juniper-count-badge ms-auto">{{ item.badge }}</span>
						</router-link>
					</li>

					<!-- SOON group (locked / coming soon) -->
					<li v-for="item in soonNavItems" :key="item.label">
						<span class="btn juniper-nav-item juniper-nav-disabled" :title="item.label + ' — coming soon'">
							<font-awesome-icon :icon="item.iconRef" class="juniper-nav-icon" />
							<span v-show="!collapsed" class="juniper-nav-label">{{ item.label }}</span>
							<font-awesome-icon v-show="!collapsed" :icon="['fas', 'lock']" class="juniper-nav-lock ms-auto" />
						</span>
					</li>

					<!-- LOWER group (active items placed below the soon group) -->
					<li v-for="item in lowerNavItems" :key="item.path">
						<router-link
							class="btn juniper-nav-item"
							:class="{ 'router-link-exact-active router-link-active': isItemActive(item) }"
							:to="item.path"
						>
							<font-awesome-icon :icon="item.iconRef" class="juniper-nav-icon" />
							<span v-show="!collapsed" class="juniper-nav-label">{{ item.label }}</span>
						</router-link>
					</li>

					<!-- ADMIN group (User Management + Settings — gated by perms) -->
					<li v-for="item in adminNavItems" :key="item.path">
						<router-link
							class="btn juniper-nav-item"
							:class="{ 'router-link-exact-active router-link-active': isItemActive(item) }"
							:to="item.path"
						>
							<font-awesome-icon :icon="item.iconRef" class="juniper-nav-icon" />
							<span v-show="!collapsed" class="juniper-nav-label">{{ item.label }}</span>
						</router-link>
						<!-- Sub-links, shown while the parent section is open -->
						<ul v-if="item.children && !collapsed && isItemActive(item)" class="list-unstyled mb-0 juniper-nav-sublist">
							<li v-for="child in item.children" :key="child.path">
								<!-- own active class: the global router-link-active styling is for top-level rows -->
								<router-link
									class="juniper-nav-subitem"
									:class="{ 'is-active': isItemActive(child) }"
									:to="child.path"
									active-class=""
									exact-active-class=""
								>
									{{ child.label }}
								</router-link>
							</li>
						</ul>
					</li>
				</ul>
			</div>

			<!-- Back to MC2 — flush with sidebar border -->
			<router-link class="btn btn-primary w-100 juniper-back-btn" to="/">
				<font-awesome-icon :icon="['fas', 'arrow-left']" :class="collapsed ? '' : 'me-2'" />
				<span v-if="!collapsed">Back to MC2</span>
			</router-link>
		</nav>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import juniperApi from '@/api/juniperApi'
import juniperUserMixin from '@/mixins/juniperUserMixin'

const STORAGE_KEY = 'juniper-sidebar-collapsed'

export default {
	name: 'JuniperSidebar',
	data() {
		return {
			collapsed: localStorage.getItem(STORAGE_KEY) === 'true',
			totalClients: null,
		}
	},
	computed: {
		...mapState(['token', 'authUser']),
		...mapGetters(['isAuth']),
		hasAdminAccess() {
			return (
				this.userHasOneOfNeededJuniperPermissions(['view_users', 'view_roles', 'view_permissions']) ||
				this.userHasAllNeededJuniperPermissions(['develop'])
			)
		},
		// ─── Nav item registry ────────────────────────────────────────
		// Three groups: top (hero items), middle (active features, alpha
		// sorted), soon (locked / coming soon). Each item:
		//   label    — display text + search target
		//   path     — router link
		//   iconRef  — FA icon tuple ['fas', 'name']
		//   match    — optional fn(routePath) → bool for active state
		//   badge    — optional number/string for count pill on right
		//   show     — optional bool gate (permissions)
		//   children — optional sub-links [{ label, path, match }] (admin group),
		//              listed under the item while its section is active
		topNavItems() {
			return [
				{
					label: 'Dashboard',
					path: '/juniper',
					iconRef: ['fas', 'house-user'],
					match: p => p === '/juniper',
				},
			]
		},
		middleNavItems() {
			// Active feature items: Clients · Themes.
			return [
				{
					label: 'Clients',
					path: '/juniper/clients',
					iconRef: ['fas', 'globe'],
					match: p => p.includes('/juniper/clients'),
					badge: this.totalClients,
				},
				{
					label: 'Themes',
					path: '/juniper/themes',
					iconRef: ['fas', 'palette'],
					match: p => p.includes('/juniper/themes'),
				},
			]
		},
		soonNavItems() {
			return [
				{ label: 'Server', iconRef: ['fas', 'cloud'] },
			]
		},
		// Rendered right below the soon group (Statistics sits under Server by
		// design), above the admin items.
		lowerNavItems() {
			return [
				{
					label: 'Statistics',
					path: '/juniper/statistics',
					iconRef: ['far', 'chart-bar'],
					match: p => p.includes('/juniper/statistics'),
				},
			]
		},
		// Admin / config — render AFTER the soon group so User Management and
		// Settings sit at the very bottom of the nav. Each item is
		// permission-gated; if the user has no admin perms the group is empty.
		adminNavItems() {
			const items = []
			if (this.userHasOneOfNeededJuniperPermissions(['view_users', 'view_roles', 'view_permissions'])) {
				items.push({
					label: 'User Management',
					path: '/juniper/user-management',
					iconRef: ['fas', 'users-cog'],
					match: p => p.includes('/juniper/user-management'),
				})
			}
			if (this.userHasAllNeededJuniperPermissions(['develop'])) {
				items.push({
					label: 'Settings',
					path: '/juniper/settings',
					iconRef: ['fas', 'cog'],
					match: p => p.includes('/juniper/settings'),
				})
				items.push({
					label: 'Accessibility Stats',
					path: '/juniper/accessibility-stats',
					iconRef: ['fas', 'universal-access'],
					match: p => p.includes('/juniper/accessibility-stats'),
					children: [
						{
							label: 'List',
							path: '/juniper/accessibility-stats',
							match: p => p.includes('/juniper/accessibility-stats') && !p.includes('/juniper/accessibility-stats/engine'),
						},
						{
							label: 'Engine stats',
							path: '/juniper/accessibility-stats/engine',
							match: p => p.includes('/juniper/accessibility-stats/engine'),
						},
					],
				})
				items.push({
					label: 'Logs',
					path: '/juniper/logs',
					iconRef: ['fas', 'list'],
					match: p => p.includes('/juniper/logs'),
				})
			}
			// Debugging sits below Settings and is developer-only for now —
			// gated by email prefix, not permissions.
			if (this.canSeeDebugging) {
				items.push({
					label: 'Debugging',
					path: '/juniper/debugging',
					iconRef: ['fas', 'bug'],
					match: p => p.includes('/juniper/debugging'),
				})
			}
			return items
		},
		canSeeDebugging() {
			const email = (this.authUser && this.authUser.email) || ''
			return email.startsWith('srdjan') || email.startsWith('marko')
		},
	},
	created() {
		this.$emit('toggle', this.collapsed)
		this.loadClientCount()
	},
	methods: {
		toggleCollapsed() {
			this.collapsed = !this.collapsed
			localStorage.setItem(STORAGE_KEY, this.collapsed)
			this.$emit('toggle', this.collapsed)
		},
		loadClientCount() {
			juniperApi
				.get('/clients', { params: { all: true } })
				.then(({ data }) => {
					this.totalClients = data.length
				})
				.catch(() => {})
		},
		// Run the item's `match` predicate against the current route path
		// to decide whether to apply the active class.
		isItemActive(item) {
			if (typeof item.match !== 'function') return false
			return item.match(this.$route.path)
		},
	},
	mixins: [juniperUserMixin],
}
</script>
